<template>
  <b-form-group :label="displayLabel" label-class="label-style" :description="labelPeriod">
    <flat-pickr :ref="`flatpickr-${instanceId}`" v-model="dateRangeModel" :class="`custom-select flatpickr`"
      :config="configFlatPickr" @on-change="onSelectedDatesChange" @on-close="onPickerClose" />
  </b-form-group>
</template>

<script>
import { mapState } from "vuex";
import moment from "moment";
import { dateFormatEnum } from "@/filter/dateFormat";
import Tools from "@/utils/tools";
import dimensionEnum from "@/model/dimensionEnum";

export default {
  name: "DateRangePicker",
  components: {},
  props: {
    // ID unique pour éviter les conflits entre plusieurs instances
    instanceId: {
      type: String,
      default() {
        return `${Math.random().toString(36).substr(2, 9)}`;
      }
    },
    allowFutureDate: {
      type: Boolean,
      default() {
        return false;
      }
    },
    /**  Dimension sur laquelle faire porter le filtre, à override dans le cas où on a plusieurs dates sur lesquels on veut filtrer pour une même entité*/
    dateDimension: {
      type: String,
      default() {
        return dimensionEnum.DATE;
      }
    },
    /** Format d'affichage dans le date picker */
    datePickerFormat: {
      type: String,
      default() {
        return dateFormatEnum.DATE_SHORT_FORMAT;
      },
    },
    /** Format de date attendu dans les filtres */
    dateFilterFormat: {
      type: String,
      default() {
        return dateFormatEnum.SQL_DATE_FORMAT;
      },
    },
    labelPeriodPrefix: {
      type: String,
      default() {
        return this.$t("filter.period_default_prefix");
      },
    },
    /** Pour indiquer des dates à imposer au picker à tout moment : [ debut, fin ], dates en string au format datePickerFormat.
     * Elles seront plus prioritaires que les dates stockées depuis une autre page,
     * et les remplaceront même dans le storage si useStoredDateRange = true
     */
    dateRangeToForce: {
      type: Array,
      default() {
        return [];
      },
    },
    /**
     * Indique s'il faut utiliser les dates stockées depuis une autre page au lieu de la date du jour par défaut.
     * Elles seront moins prioritaires que dateRangeToForce.
     */
    useStoredDateRange: {
      type: Boolean,
      default() {
        return true;
      },
    },
    /**
     * Heures de début et de fin provenant du filtre TimeRangePicker
     */
    timeRangeValue: {
      type: Object,
      default() {
        return null;
      },
    },
    /**
     * Indique si le filtre de temps est activé dans le composant parent
     */
    useTimeFilter: {
      type: Boolean,
      default() {
        return true;
      },
    },
    /**
     * Mode sélection de mois uniquement (affiche seulement les mois complets)
     */
    useMonthOnly: {
      type: Boolean,
      default() {
        return false;
      },
    },
    useMonthLabel: {
      type: Boolean,
      default() {
        return false;
      },
    },
  },
  data: function () {
    return {
      dateRangeModel: "",
      dateRangeStr: "",
      selectedDates: [],
      dateRangeFilter: null,
      storageKey: "DateRangePicker",
      timeRangeWatchedValue: null,
    };
  },
  computed: mapState({
    // Référence dynamique au flatpickr pour éviter les conflits
    flatpickrRef() {
      return this.$refs[`flatpickr-${this.instanceId}`];
    },
    configFlatPickr() {
      const config = Tools.Helper.getFlatPickerConfig(this.datePickerFormat, true, this.allowFutureDate, this.useMonthOnly);

      // Ajouter defaultDate pour que le flatpickr s'ouvre sur la bonne année
      if (this.dateRangeToForce && this.dateRangeToForce.length) {
        const defaultDate = this.dateRangeToForce.length == 1
          ? this.dateRangeToForce[0]
          : this.dateRangeToForce[0];
        config.defaultDate = defaultDate;
      }

      return config;
    },
    startDateMoment() {
      if (this.selectedDates && this.selectedDates.length == 2) {
        return moment(this.selectedDates[0], this.datePickerFormat);
      } else return null;
    },
    endDateMoment() {
      if (this.selectedDates && this.selectedDates.length == 2) {
        return moment(this.selectedDates[1], this.datePickerFormat);
      } else return null;
    },
    startTime() {
      if (this.timeRangeWatchedValue) {
        if (!this.timeRangeWatchedValue.isFullRange) {
          var hours = this.timeRangeWatchedValue.timeRangeFilter.values;
          return hours[0];
        } else return process.env.VUE_APP_START_DAY;
      }
      return null;
    },
    endTime() {
      if (this.timeRangeWatchedValue) {
        if (!this.timeRangeWatchedValue.isFullRange) {
          var hours = this.timeRangeWatchedValue.timeRangeFilter.values;
          var lastHour = parseInt(hours[hours.length - 1]);
          if (lastHour === parseInt(process.env.VUE_APP_START_DAY)) {
            var penultimateHour = parseInt(hours[hours.length - 2]);
            return (penultimateHour + 1).toString();
          }
          return (lastHour + 1).toString();
        } else return process.env.VUE_APP_START_DAY;
      }
      return null;
    },
    isPeriodValid() {
      return (
        this.startDateMoment &&
        this.startDateMoment.isValid() &&
        this.endDateMoment &&
        this.endDateMoment.isValid()
      );
    },
    labelPeriod() {
      if (this.useMonthLabel || this.useMonthOnly) {
        if (!this.isPeriodValid) {
          return "";
        }
        // Affichage par mois
        return this.$t("filter.period_default_prefix") + this.$t("filter.period_full", {
          0: this.startDateMoment.format('DD/MM/YYYY'),
          1: this.endDateMoment.format('DD/MM/YYYY')
        });
      }

      return this.isPeriodValid
        ? Tools.BusinessDateHelper.getLabelPeriod(
          this.startDateMoment,
          this.endDateMoment,
          this.labelPeriodPrefix,
          this.startTime ?? process.env.VUE_APP_START_DAY,
          this.endTime ?? process.env.VUE_APP_START_DAY,
          this.useTimeFilter
        )
        : "";
    },
    displayLabel() {
      return this.useMonthOnly || this.useMonthLabel ? this.$t("filter.period_default_prefix") : this.$t("filter.days");
    },
  }),
  watch: {
    dateRangeToForce(newValue) {
      if (newValue && newValue.length) {
        this.dateRangeModel =
          newValue.length == 1 ? newValue.concat(newValue) : newValue;
      }
    },
    timeRangeValue(newVal) {
      this.timeRangeWatchedValue = newVal;
    },
    labelPeriod(newValue) {
      this.$emit("labelPeriodHoursUpdated", {
        labelPeriod: newValue,
      });
    }
  },
  mounted: function () {
    this.setDefaultRange();
  },
  methods: {
    onSelectedDatesChange(selectedDates, str) {
      if (this.useMonthLabel && selectedDates && selectedDates.length >= 1) {
        if (!this.useMonthOnly && selectedDates.length == 2) {
          //Sans le mode mois uniquement, mais avec l'option month-label (affichage des mois sélectionnés)
          const startDateOriginal = selectedDates[0] instanceof Date ? moment(selectedDates[0]) : moment(selectedDates[0], this.datePickerFormat);
          const endDateOriginal = selectedDates[1] instanceof Date ? moment(selectedDates[1]) : moment(selectedDates[1], this.datePickerFormat);

          // Récupérer le 1er jour du mois de la première date et le dernier jour du mois de la deuxième date
          const startDate = startDateOriginal.clone().startOf('month');
          const today = moment().startOf('day');
          const endDate = endDateOriginal.clone().endOf('month').isAfter(today) ? today : endDateOriginal.clone().endOf('month');

          this.selectedDates = [startDate.toDate(), endDate.toDate()];
          this.dateRangeStr = startDateOriginal.format('MMMM YYYY')[0].toUpperCase() + startDateOriginal.format('MMMM YYYY').slice(1)
            + ' - ' +
            endDateOriginal.format('MMMM YYYY')[0].toUpperCase() + endDateOriginal.format('MMMM YYYY').slice(1);

          // Synchronise flatpickr
          this.$nextTick(() => {
            if (this.flatpickrRef && this.flatpickrRef.fp) {
              // Selectionne la plage de dates sans déclencher on-change pour éviter boucle
              this.flatpickrRef.fp.setDate([startDate.toDate(), endDate.toDate()], false);
              // Met l'input date-range en format 'MMMM YYYY - MMMM YYYY'
              this.flatpickrRef.fp.input.value = this.dateRangeStr;
            }
          });
        }
        else if (this.useMonthOnly && selectedDates.length >= 1) {
          //En mode mois uniquement, mais avec l'option month-label (affichage des mois sélectionnés)
          const selectedDate = selectedDates[0] instanceof Date ? moment(selectedDates[0]) : moment(selectedDates[0], this.datePickerFormat);
          const startOfMonth = selectedDate.clone().startOf('month');
          const rawEnd = selectedDate.clone().endOf('month');
          const today = moment().startOf('day');
          const endOfMonth = rawEnd.isAfter(today) && selectedDate.isSame(today, 'month') ? today : rawEnd;

          this.selectedDates = [startOfMonth.toDate(), endOfMonth.toDate()];
          this.dateRangeStr = selectedDate.format('MMMM YYYY')[0].toUpperCase() + selectedDate.format('MMMM YYYY').slice(1);

          // Synchronise flatpickr
          this.$nextTick(() => {
            if (this.flatpickrRef && this.flatpickrRef.fp) {
              // Selectionne la plage de dates sans déclencher on-change pour éviter boucle
              this.flatpickrRef.fp.setDate([startOfMonth.toDate(), endOfMonth.toDate()], false);
              // Met l'input date-range en format 'MMMM YYYY'
              this.flatpickrRef.fp.input.value = this.dateRangeStr;
            }
          });
        }
      }
      else if (selectedDates && selectedDates.length == 2) {
        this.selectedDates = selectedDates;
        this.dateRangeStr = str;
      } else {
        this.selectedDates = [];
        this.dateRangeStr = "";
      }
      this.updateDateFilters();
    },
    onPickerClose(selectedDates, str) {
      if (selectedDates && selectedDates.length == 1) {
        // Si une seule date a été choisie et le picker refermé, on force le range à cette seule date
        this.dateRangeModel = [str, str];
      }
    },
    setDefaultRange() {
      // NB : pour forcer une valeur au flatpickr, il faut lui passer la range en tableau de date dans le v-model,
      // alors qu'ensuite le flatpickr le renseigne avec le libellé (ex: "01/01/2020 au 05/01/2020", localisé d'après l'init de flatpickr dans main.js)
      if (this.dateRangeToForce && this.dateRangeToForce.length) {
        this.dateRangeModel =
          this.dateRangeToForce.length == 1
            ? this.dateRangeToForce.concat(this.dateRangeToForce)
            : this.dateRangeToForce;
      } else {
        const storedRange = Tools.StorageHelper.getStoredDateRangeSelectedValues(
          this.storageKey,
          this.datePickerFormat
        );
        if (this.useStoredDateRange && storedRange && storedRange.length) {
          this.dateRangeModel =
            storedRange.length == 1
              ? storedRange.concat(storedRange)
              : storedRange;
        } else {
          const defaultDate = Tools.BusinessDateHelper.getTodaysBusinessDate()
            .format(this.datePickerFormat)
            .toString();
          this.dateRangeModel = [defaultDate, defaultDate];
        }
      }
    },
    updateDateFilters() {
      if (this.isPeriodValid) {
        const startDateForFilter = this.startDateMoment
          .format(this.dateFilterFormat)
          .toString();
        const endDateForFilter = this.endDateMoment
          .format(this.dateFilterFormat)
          .toString();

        const values = [startDateForFilter, endDateForFilter];

        this.dateRangeFilter = {
          dimension: this.dateDimension,
          dateRange: values,
        };
        if (this.useStoredDateRange) {
          Tools.StorageHelper.storeDateRangeSelectedValues(
            this.storageKey,
            values,
            this.dateFilterFormat
          );
        }
      } else {
        this.dateRangeFilter = null;
        if (this.useStoredDateRange) {
          Tools.StorageHelper.storeDateRangeSelectedValues(
            this.storageKey,
            null,
            null
          );
        }
      }
      this.$emit("dateRangeFilterUpdated", {
        dateRangeFilter: this.dateRangeFilter,
        dateRangeStr: this.dateRangeStr,
        labelPeriod: this.labelPeriod,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.flatpickr-input {
  background: none;
}

.flatpickr-confirm {
  background-color: white !important;
}

// Cibler la description du form-group Bootstrap Vue
::v-deep .form-text.text-muted {
  white-space: pre-line !important;
}

.label-style {
  flex: 1;
  text-align: left;
  font-weight: bold;
}
</style>
